import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Eye, Mail, Save, Users, FileDown, Settings, ChevronsUpDown, ChevronRight } from 'lucide-react';
import { AcceptanceBadge } from './AcceptanceBadge';

const CTYPES = [
  'Concrete', 'Iron', 'Tin Tile', 'Painting', 'Butynol',
  'Guttering', 'Maintenance', 'Repair', 'Commercial',
];

const SECTION_COLOURS = {
  Concrete:    'bg-slate-100 text-slate-800',
  Iron:        'bg-orange-100 text-orange-800',
  'Tin Tile':  'bg-zinc-100 text-zinc-800',
  Painting:    'bg-purple-100 text-purple-800',
  Butynol:     'bg-emerald-100 text-emerald-800',
  Guttering:   'bg-sky-100 text-sky-800',
  Maintenance: 'bg-amber-100 text-amber-800',
  Repair:      'bg-rose-100 text-rose-800',
  Commercial:  'bg-blue-100 text-blue-800',
};

function fmtMoney(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtUnit(v) {
  const n = Number(v || 0);
  return n.toLocaleString('en-NZ', { maximumFractionDigits: 2 });
}

// Iter258 — Component-type display rename map. Source-of-truth for the
// `component_type` value in the DB stays "Aquaseal" (keeps all the
// Iter233/234/242/256/257 link cascades + BOM rollups working), but
// every operator-facing surface should read "Dektite". Add future
// renames here rather than scattering aliases.
const COMPONENT_TYPE_LABELS = {
  Aquaseal: 'Dektite',
};
const displayComponentType = (t) => COMPONENT_TYPE_LABELS[t] || t;

// Iter258d — Display-only parser that splits a raw `type_name` into a
// "Dimension" (size / spec pattern) and a "Type" (trailing classifier
// like Flue / Pipe / Corru). Only applies on the Contractor sub-tables
// for Dektite / Barge / Flashing — sections where the catalogue rows
// embed multiple concepts in a single string and the section header
// already tells the operator which component family they're looking at.
//
// Parsing is conservative: when no dimension is detected the whole
// (de-prefixed, material-stripped) name falls back into Dimension and
// Type renders as `—`. Operator-friendly — never silently hides info.
const _SECTIONS_TO_SPLIT = new Set(['Aquaseal', 'Barge', 'Flashing']);

const _stripWords = (s, words) => {
  let out = s;
  words.forEach(w => {
    out = out.replace(new RegExp(`\\b${w}\\b`, 'gi'), '');
  });
  return out.replace(/\s{2,}/g, ' ').replace(/^[\s,/-]+|[\s,/-]+$/g, '').trim();
};

const parseTypeName = (componentType, rawName) => {
  const name = String(rawName || '').trim();
  if (!name) return { dimension: '', subType: '' };
  if (!_SECTIONS_TO_SPLIT.has(componentType)) return { dimension: name, subType: '' };

  if (componentType === 'Aquaseal') {
    // Strip Dektite-family noise words (section name redundant; "BLACK"
    // is the EPDM colour — useful only when contrasting with Lead/Ali
    // variants which already have their own catalogue rows).
    let stripped = _stripWords(name, ['Dektite', 'BLACK', 'WITH', 'CLIPS', 'Aluminium', 'Clip']);
    // Leading numbered tag like "#1 ", "#12 " — drop it.
    stripped = stripped.replace(/^#\d+\s*/i, '').trim();
    // Dimension: a range "X-Ymm" OR a single "Xmm".
    const rangeM = stripped.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*mm/i);
    const singleM = stripped.match(/(\d+(?:\.\d+)?)\s*mm/i);
    let dimension = '';
    if (rangeM) dimension = `${rangeM[1]}-${rangeM[2]}mm`;
    else if (singleM) dimension = `${singleM[1]}mm`;
    // Sub-type: trailing classifier (Flue / Pipe / Toilet Breather / Hot water / Retrofit).
    const lower = stripped.toLowerCase();
    let subType = '';
    if (/flue/.test(lower)) subType = 'Flue';
    else if (/toilet|breather/.test(lower)) subType = 'Toilet Breather';
    else if (/hot water/.test(lower)) subType = 'Hot water';
    else if (/pipe/.test(lower)) subType = 'Pipe';
    else if (/retrofit/.test(lower)) subType = 'Retrofit';
    if (!dimension) return { dimension: stripped || name, subType };
    return { dimension, subType };
  }

  if (componentType === 'Barge' || componentType === 'Flashing') {
    // Strip section prefix + material words the user flagged as
    // irrelevant for the contractor view ("Galv", "Alu", "Aluminium").
    let stripped = _stripWords(name, [componentType, 'Galv', 'Galvanised', 'Galvanized', 'Alu', 'Aluminium', 'COL', 'Colorsteel']);
    // Dimension patterns common in this codebase:
    //   • "0.55 300G/4F" or ".055 300G/4F"   (thickness mm + gauge + folds)
    //   • "1000W/3F"                         (width + folds)
    //   • "225G/6F"                          (gauge + folds, no thickness)
    const dimMatch = stripped.match(/(\d*\.\d+\s+)?(\d+\s*[WG]\s*\/\s*\d+F)/i);
    let dimension = '';
    if (dimMatch) {
      dimension = (dimMatch[1] ? dimMatch[1].trim() + ' ' : '') + dimMatch[2].replace(/\s+/g, '');
      stripped = stripped.replace(dimMatch[0], '').replace(/\s{2,}/g, ' ').trim();
    }
    // Whatever's left after stripping the dimension is the Type word
    // (e.g. "Corru", "Trapz", "Butynol C/S"). Cap at ~22 chars so the
    // column doesn't overflow on edge cases.
    const subType = stripped.length > 22 ? stripped.slice(0, 22) + '…' : stripped;
    if (!dimension) return { dimension: subType || name, subType: '' };
    return { dimension, subType };
  }

  return { dimension: name, subType: '' };
};


// Iter268 — Split cell. Renders the per-Type-row checkbox + percent +
// source kind / source-id picker. Fans the patch up to the parent via
// `onChange`. Kept as a small dedicated component so the table row
// stays readable.
const SPLIT_KIND_OPTIONS = [
  { value: 'rtr',    label: 'RTR' },
  { value: 'person', label: 'Contractor (person)' },
  { value: 'type',   label: 'Contractor Type' },
];
const SplitCell = ({ tRow, contractors, currentContractorTypeId, onChange }) => {
  const enabled = !!tRow.split_enabled;
  const percent = tRow.split_percent ?? 0;
  const kind    = tRow.split_source_kind || '';
  const sourceId = tRow.split_source_id || '';
  const splitShare = tRow.split_share || 0;
  // Exclude the currently-assigned contractor from the person picker
  // (a row should not split to itself per Iter268-b).
  const personOptions = (contractors || []).filter(c => String(c._id || c.id) !== String(currentContractorTypeId));
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid={`split-toggle-${tRow.type_name}`}
          checked={enabled}
          onChange={(e) => onChange({
            split_enabled: e.target.checked,
            split_percent: e.target.checked ? (percent || 0) : 0,
            split_source_kind: e.target.checked ? (kind || 'rtr') : '',
            split_source_id: e.target.checked ? sourceId : '',
          })}
          className="accent-amber-600"
        />
        Split
      </label>
      {enabled && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0} max={100} step={1}
              data-testid={`split-percent-${tRow.type_name}`}
              value={percent}
              placeholder={tRow.split_percent === null ? 'mixed' : '0'}
              onChange={(e) => onChange({ split_percent: e.target.value })}
              className="w-12 text-right border border-amber-300 rounded px-1 py-0.5 text-[11px] tabular-nums bg-white"
              title="Contractor's share — the remainder is owed to the Split Source."
            />
            <span className="text-[10px] text-slate-500">% to Con</span>
          </div>
          <select
            data-testid={`split-kind-${tRow.type_name}`}
            value={kind}
            onChange={(e) => onChange({ split_source_kind: e.target.value, split_source_id: '' })}
            className="w-full text-[11px] border border-amber-300 rounded px-1 py-0.5 bg-white"
            title="Where the remainder of the contractor payment goes."
          >
            <option value="">— Source kind —</option>
            {SPLIT_KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {kind === 'person' && (
            <select
              data-testid={`split-source-person-${tRow.type_name}`}
              value={sourceId}
              onChange={(e) => onChange({ split_source_id: e.target.value })}
              className="w-full text-[11px] border border-amber-300 rounded px-1 py-0.5 bg-white"
            >
              <option value="">— Pick contractor —</option>
              {personOptions.map(c => (
                <option key={c._id || c.id} value={c._id || c.id}>
                  {c.contractor_name || c.company_name || '(unnamed)'}
                </option>
              ))}
            </select>
          )}
          {kind === 'type' && (
            <select
              data-testid={`split-source-type-${tRow.type_name}`}
              value={sourceId}
              onChange={(e) => onChange({ split_source_id: e.target.value })}
              className="w-full text-[11px] border border-amber-300 rounded px-1 py-0.5 bg-white"
            >
              <option value="">— Pick type —</option>
              {['Concrete','Iron','Tin Tile','Painting','Butynol','Guttering','Maintenance','Repair','Commercial']
                .map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <div className="text-[10px] text-amber-800 tabular-nums">
            <b>{(100 - (percent || 0)).toFixed(0)}%</b> → Split (${splitShare.toFixed(2)})
          </div>
        </div>
      )}
    </div>
  );
};

export default function ContractorOverviewTab({ api, appraisalId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState({});
  const [emailModal, setEmailModal] = useState(null);
  // dirty: line_key -> { contractor_type?, contractor_rate?, labour_rate?, work_description? }
  const [dirty, setDirty] = useState({});
  // Cache of rate-options per component_type (lowercase).
  const [rateOptionsCache, setRateOptionsCache] = useState({});

  // Iter269 — bulkConRate state removed. Bulk Con $ override is gone;
  // rate changes go through the per-section "Edit rates" link.

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const [ov, cons] = await Promise.all([
        api.get(`/appraisals/${appraisalId}/contractor-overview`),
        api.get('/contractors'),
      ]);
      setData(ov.data || ov);
      setContractors((cons.data || cons) || []);
      setDirty({});
    } catch (err) {
      toast.error(`Failed to load contractor overview: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const setLine = (lineKey, patch) => {
    setDirty(prev => ({
      ...prev,
      [lineKey]: { ...(prev[lineKey] || {}), ...patch },
    }));
  };

  const fetchRateOptions = useCallback(async (component) => {
    if (!component) return [];
    const key = component.toLowerCase().split(' ')[0];
    if (rateOptionsCache[key]) return rateOptionsCache[key];
    try {
      const { data: r } = await api.get(`/contractor-reports/rate-options/${encodeURIComponent(component)}`);
      const opts = r?.options || [];
      setRateOptionsCache(c => ({ ...c, [key]: opts }));
      return opts;
    } catch {
      return [];
    }
  }, [api, rateOptionsCache]);

  const saveDirty = async () => {
    // Iter269 — strip contractor_rate / labour_rate from any dirty
    // entry before posting. The Contractor Overview UI no longer
    // creates rate overrides on the appraisal; only contractor_type,
    // work_description, contractor_id and split fields persist.
    const overrides = Object.entries(dirty).map(([line_key, v]) => {
      const { contractor_rate, labour_rate, ...rest } = v || {};
      void contractor_rate; void labour_rate;
      return { line_key, ...rest };
    });
    if (!overrides.length) { toast.info('No changes to save'); return; }
    try {
      await api.put(`/appraisals/${appraisalId}/contractor-overrides`, { overrides });
      toast.success(`Saved ${overrides.length} change${overrides.length === 1 ? '' : 's'}`);
      await load();
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const assignTypeContractor = async (ctype, contractor_id) => {
    const current = data?.type_assignments || {};
    const next = { ...current, [ctype]: contractor_id };
    if (!contractor_id) delete next[ctype];
    setSavingKeys(s => ({ ...s, [`type:${ctype}`]: true }));
    try {
      await api.put(`/appraisals/${appraisalId}/contractor-overrides`, { type_assignments: next });
      toast.success(`Assigned ${ctype} contractor`);
      await load();
    } catch (err) {
      toast.error(`Assign failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSavingKeys(s => ({ ...s, [`type:${ctype}`]: false }));
    }
  };

  // Iter133 — Bulk-set numeric rate (lab/con) across every row in the
  // unified work list. Queues as dirty overrides so the Save button
  // still gates the write.
  // Iter269 — bulkSetRate removed. Per-line + bulk Con $ overrides
  // are no longer set on the appraisal; the operator changes
  // contractor rates exclusively through the "Edit rates" link to
  // Component Rates Tables.

  // Iter133 — Bulk "Change all" for the Contractor Type column. Sets
  // every row's contractor_type in one go (queues as dirty overrides
  // so the operator can review and Save). `force=true` overwrites rows
  // already changed; default skips them.
  const bulkSetContractorType = (newType, force = false) => {
    if (!newType || !liveRows.length) return;
    const patch = {};
    let touched = 0;
    liveRows.forEach(r => {
      const existing = dirty[r.line_key]?.contractor_type ?? r.contractor_type;
      if (!force && existing === newType) return;
      patch[r.line_key] = { ...(dirty[r.line_key] || {}), contractor_type: newType };
      touched += 1;
    });
    if (!touched) {
      toast.info(`All rows are already assigned to ${newType}.`);
      return;
    }
    setDirty(prev => ({ ...prev, ...patch }));
    toast.success(`${newType} applied to ${touched} row${touched === 1 ? '' : 's'} — press Save to commit.`);
  };

  const openPdf = (ctype, download = false) => {
    const url = `${api.defaults.baseURL}/appraisals/${appraisalId}/contractor-pdf/${encodeURIComponent(ctype)}${download ? '?download=1' : ''}`;
    window.open(url, '_blank');
  };

  // Iter275 — Contractor Overview PDF (totals only)
  const openOverviewPdf = (ctype, download = false) => {
    const url = `${api.defaults.baseURL}/appraisals/${appraisalId}/contractor-overview-pdf/${encodeURIComponent(ctype)}${download ? '?download=1' : ''}`;
    window.open(url, '_blank');
  };

  // ── Derived live rows (apply dirty edits on top of server data) ──
  const liveRows = useMemo(() => {
    if (!data?.lines) return [];
    return data.lines.map(row => {
      const d = dirty[row.line_key] || {};
      const lab = d.labour_rate !== undefined && d.labour_rate !== '' ? Number(d.labour_rate) : Number(row.labour_rate || 0);
      const con = d.contractor_rate !== undefined && d.contractor_rate !== '' ? Number(d.contractor_rate) : Number(row.contractor_rate || 0);
      const ctype = d.contractor_type || row.contractor_type;
      const desc = d.work_description || row.description;
      const units = row.total || 0;
      // Iter268 — merge dirty split fields.
      const splitEnabled = d.split_enabled !== undefined ? !!d.split_enabled : !!row.split_enabled;
      const splitPercent = d.split_percent !== undefined && d.split_percent !== ''
        ? Number(d.split_percent) : Number(row.split_percent || 0);
      const splitKind = d.split_source_kind !== undefined ? d.split_source_kind : (row.split_source_kind || '');
      const splitId   = d.split_source_id   !== undefined ? d.split_source_id   : (row.split_source_id || '');
      const conTotal = +(units * con).toFixed(2);
      const conShare = splitEnabled ? +(conTotal * splitPercent / 100).toFixed(2) : conTotal;
      const splitShare = splitEnabled ? +(conTotal - conShare).toFixed(2) : 0;
      // Iter273 — exclude from PDF flag (dirty wins over server value).
      const excludedFromReport = d.excluded_from_report !== undefined
        ? !!d.excluded_from_report : !!row.excluded_from_report;
      return {
        ...row,
        description: desc,
        labour_rate: lab,
        contractor_rate: con,
        contractor_type: ctype,
        labour_total: +(units * lab).toFixed(2),
        contractor_total: conTotal,
        split_enabled: splitEnabled,
        split_percent: splitPercent,
        split_source_kind: splitKind,
        split_source_id: splitId,
        contractor_share: conShare,
        split_share: splitShare,
        excluded_from_report: excludedFromReport,
      };
    });
  }, [data, dirty]);

  // Iter160 — Per-Component-Type grouped sections (mirrors the Quote
  // Calculation Summary layout). Each section is a Component Type
  // (Aquaseal / Ridge / Spouting / etc.) holding sub-rows grouped by
  // Type Name (`component`). Editing a Type row's Lab/Con rate or
  // Contractor Type fans out to every underlying line via the same
  // dirty-overrides mechanism — so saving still goes through a single
  // PUT /contractor-overrides call.
  const componentSections = useMemo(() => {
    if (!liveRows.length) return [];
    // First group by component_type (description), then by type_name (component).
    const byCType = {};
    liveRows.forEach(r => {
      const cType = r.description || 'Misc';
      if (!byCType[cType]) byCType[cType] = {};
      const tName = r.component || 'Unspecified';
      if (!byCType[cType][tName]) {
        byCType[cType][tName] = { type_name: tName, profile: r.profile || '', lines: [] };
      }
      byCType[cType][tName].lines.push(r);
    });
    return Object.entries(byCType).map(([cType, typeMap]) => {
      const typeRows = Object.values(typeMap).map(g => {
        const total_units  = g.lines.reduce((s, l) => s + (l.total || 0), 0);
        const total_qty    = g.lines.reduce((s, l) => s + (l.qty || 0), 0);
        const total_length = g.lines.reduce((s, l) => s + (l.length || 0), 0);
        const labour_total = g.lines.reduce((s, l) => s + (l.labour_total || 0), 0);
        const contractor_total = g.lines.reduce((s, l) => s + (l.contractor_total || 0), 0);
        // Lab/Con rate: 'Mixed' when underlying lines differ, single value otherwise
        const labRates = Array.from(new Set(g.lines.map(l => +Number(l.labour_rate || 0).toFixed(4))));
        const conRates = Array.from(new Set(g.lines.map(l => +Number(l.contractor_rate || 0).toFixed(4))));
        const labour_rate     = labRates.length === 1 ? labRates[0] : null;
        const contractor_rate = conRates.length === 1 ? conRates[0] : null;
        const ctypes = Array.from(new Set(g.lines.map(l => l.contractor_type)));
        const contractor_type = ctypes.length === 1 ? ctypes[0] : null;
        // Iter262 — rollup Flashing Type across lines. Unique non-empty
        // values comma-joined; empty string when nothing tagged. The
        // contractor table's Type column reads this for Flashing rows.
        const ftypes = Array.from(new Set(g.lines.map(l => (l.flashing_type || '').trim()).filter(Boolean)));
        const flashing_type = ftypes.join(', ');
        // Iter268 — split rollup. Mixed across lines → null sentinel.
        const splitFlags = Array.from(new Set(g.lines.map(l => !!l.split_enabled)));
        const splitPcts  = Array.from(new Set(g.lines.map(l => +Number(l.split_percent || 0).toFixed(2))));
        const splitKinds = Array.from(new Set(g.lines.map(l => l.split_source_kind || '')));
        const splitIds   = Array.from(new Set(g.lines.map(l => l.split_source_id || '')));
        const split_enabled = splitFlags.length === 1 ? splitFlags[0] : null;
        const split_percent = splitPcts.length === 1 ? splitPcts[0] : null;
        const split_source_kind = splitKinds.length === 1 ? splitKinds[0] : null;
        const split_source_id   = splitIds.length === 1 ? splitIds[0] : null;
        const split_share = g.lines.reduce((s, l) => s + (l.split_share || 0), 0);
        const contractor_share = g.lines.reduce((s, l) => s + (l.contractor_share || 0), 0);
        // Iter273 — excluded rollup. all|some|none. The Type-row's
        // checkbox is "All included" when all underlying lines are
        // included, "Excluded" when all are flagged, and indeterminate
        // (null) otherwise.
        const exclFlags = Array.from(new Set(g.lines.map(l => !!l.excluded_from_report)));
        const excluded_from_report = exclFlags.length === 1 ? exclFlags[0] : null;
        const excluded_count = g.lines.filter(l => l.excluded_from_report).length;
        // Per-Type any dirty? helps highlight the row.
        const isDirty = g.lines.some(l => !!dirty[l.line_key]);
        return {
          type_name: g.type_name,
          profile: g.profile,
          flashing_type,
          lines: g.lines,
          line_count: g.lines.length,
          total_qty,
          total_length,
          total_units,
          labour_rate,
          contractor_rate,
          labour_total,
          contractor_total,
          contractor_type,
          split_enabled,
          split_percent,
          split_source_kind,
          split_source_id,
          split_share,
          contractor_share,
          excluded_from_report,
          excluded_count,
          isDirty,
        };
      }).sort((a, b) => a.type_name.localeCompare(b.type_name));
      const section_lab = typeRows.reduce((s, r) => s + r.labour_total, 0);
      const section_con = typeRows.reduce((s, r) => s + r.contractor_total, 0);
      const section_units = typeRows.reduce((s, r) => s + r.total_units, 0);
      const section_lines = typeRows.reduce((s, r) => s + r.line_count, 0);
      const section_split = typeRows.reduce((s, r) => s + (r.split_share || 0), 0);
      const section_net   = typeRows.reduce((s, r) => s + (r.contractor_share || 0), 0);
      const section_excluded = typeRows.reduce((s, r) => s + (r.excluded_count || 0), 0);
      return { component_type: cType, type_rows: typeRows, section_lab, section_con, section_units, section_lines, section_split, section_net, section_excluded };
    }).sort((a, b) => a.component_type.localeCompare(b.component_type));
  }, [liveRows, dirty]);

  // Set rate / contractor_type on every underlying line of a Type row.
  const setTypeRowField = (typeRow, field, value) => {
    const next = { ...dirty };
    typeRow.lines.forEach(l => {
      next[l.line_key] = { ...(next[l.line_key] || {}), [field]: value };
    });
    setDirty(next);
  };

  // Iter268 — fan-out a multi-field split patch across every line of a
  // Type row (or every line in a section, or every live row).
  const setLinesSplit = (lines, patch) => {
    const next = { ...dirty };
    lines.forEach(l => {
      next[l.line_key] = { ...(next[l.line_key] || {}), ...patch };
    });
    setDirty(next);
  };
  // Bulk-apply a split to ALL lines in the appraisal.
  const bulkSplitAll = (patch) => setLinesSplit(liveRows, patch);
  // Bulk-apply a split to all lines in one Component Type section.
  const bulkSplitSection = (componentTypeKey, patch) => {
    const lines = liveRows.filter(r => (r.description || 'Misc') === componentTypeKey);
    setLinesSplit(lines, patch);
  };
  // Iter268b — bulk-split form state. The same form drives both
  // "Apply to whole appraisal" and per-section "Apply to this section".
  const [bulkSplit, setBulkSplit] = useState({
    enabled: true,
    percent: 50,
    source_kind: 'rtr',
    source_id: '',
  });
  // Iter268c — split presets. Each preset patches the bulkSplit state;
  // operator can then click an "Apply" button to fan it out. "Clear"
  // wipes splits across the chosen scope.
  const SPLIT_PRESETS = [
    { label: '50 / 50 → RTR',  percent: 50, source_kind: 'rtr' },
    { label: '70 / 30 → RTR',  percent: 70, source_kind: 'rtr' },
    { label: '30 / 70 → RTR',  percent: 30, source_kind: 'rtr' },
    { label: '60 / 40 → RTR',  percent: 60, source_kind: 'rtr' },
    { label: '80 / 20 → RTR',  percent: 80, source_kind: 'rtr' },
  ];
  const applyBulkSplitPatch = (scope, sectionKey = null) => {
    const patch = {
      split_enabled: !!bulkSplit.enabled,
      split_percent: bulkSplit.enabled ? Number(bulkSplit.percent || 0) : 0,
      split_source_kind: bulkSplit.enabled ? (bulkSplit.source_kind || 'rtr') : '',
      split_source_id: bulkSplit.enabled ? (bulkSplit.source_id || '') : '',
    };
    if (scope === 'all') bulkSplitAll(patch);
    else if (scope === 'section' && sectionKey) bulkSplitSection(sectionKey, patch);
  };
  const clearAllSplits = () => bulkSplitAll({
    split_enabled: false, split_percent: 0, split_source_kind: '', split_source_id: '',
  });
  const clearSectionSplits = (sectionKey) => bulkSplitSection(sectionKey, {
    split_enabled: false, split_percent: 0, split_source_kind: '', split_source_id: '',
  });

  // Aggregate totals by contractor type for the summary strip
  const summary = useMemo(() => {
    const map = {};
    liveRows.forEach(r => {
      const k = r.contractor_type;
      if (!map[k]) {
        const sec = (data?.sections || []).find(s => s.contractor_type === k);
        map[k] = {
          contractor_type: k,
          lines: 0,
          units: 0,
          labour_total: 0,
          contractor_total: 0,
          assigned_contractor_id: sec?.assigned_contractor_id || '',
          assigned_contractor_name: sec?.assigned_contractor_name || '',
        };
      }
      map[k].lines += 1;
      map[k].units += r.total || 0;
      map[k].labour_total += r.labour_total || 0;
      map[k].contractor_total += r.contractor_total || 0;
    });
    return Object.values(map).sort((a, b) => a.contractor_type.localeCompare(b.contractor_type));
  }, [liveRows, data]);

  const grandLabour     = summary.reduce((s, x) => s + x.labour_total, 0);
  const grandContractor = summary.reduce((s, x) => s + x.contractor_total, 0);
  const grandUnits      = summary.reduce((s, x) => s + x.units, 0);
  const dirtyCount = Object.keys(dirty).length;

  if (loading) return <div className="py-16 text-center text-slate-400" data-testid="contractor-overview-loading">Loading contractor overview…</div>;
  if (!data) return <div className="py-16 text-center text-slate-400">No data available.</div>;

  const sourceBanner = (() => {
    if (data.data_source === 'component_lines')
      return { tone: 'amber', text: 'Sourced from Components tab (not yet promoted to a priced Quote). Pick a Work Description from each row\'s dropdown to auto-fill Lab + Con rates from your rate tables.' };
    if (data.data_source === 'appraisal_components')
      return { tone: 'rose',  text: 'Sourced from raw diagram measurements. Pick a Work Description per row to auto-fill rates from your rate tables, or type rates manually.' };
    return null;
  })();

  return (
    <div className="space-y-5" data-testid="contractor-overview-tab">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 border-b border-slate-200 pb-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={20} className="text-orange-600" />
            All Contractor Overview
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            One unified work list. Pick the contractor type and work description per row.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="manage-rate-tables-btn"
            onClick={() => navigate('/component-rates')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-100"
          >
            <Settings size={14} /> Manage Rate Tables
          </button>
          <button
            data-testid="save-overrides-btn"
            onClick={saveDirty}
            disabled={!dirtyCount}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-600 text-white text-sm hover:bg-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={14} /> Save {dirtyCount ? `(${dirtyCount})` : ''}
          </button>
          {/* Iter274c — Generate / regenerate acceptance tokens for
              every assigned contractor section. Pre-existing accepted
              or rejected states are preserved; only pending entries
              with changed assignments get fresh tokens. */}
          <button
            data-testid="generate-acceptance-links-btn"
            onClick={async () => {
              try {
                await api.post(`/appraisals/${appraisalId}/acceptance/generate`);
                await load();
                toast.success('Acceptance links generated — use 📋 Copy link on each section to share');
              } catch (err) {
                toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-400 bg-amber-50 text-amber-900 text-sm hover:bg-amber-100"
            title="Generate fresh acceptance links for every contractor section. Existing Accepted/Rejected statuses are preserved."
          >
            <Mail size={14} /> Generate links
          </button>
          <button
            data-testid="view-all-pdf-btn"
            onClick={() => openPdf('All')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-100"
          >
            <Eye size={14} /> Preview All
          </button>
          {/* Iter275 — Contractor Overview PDF (totals only) */}
          <button
            data-testid="preview-overview-pdf-btn"
            onClick={() => openOverviewPdf('All')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm hover:bg-amber-100"
            title="Open a condensed overview report (totals only, no per-line detail)"
          >
            <Eye size={14} /> Preview Overview
          </button>
          <button
            data-testid="download-all-pdf-btn"
            onClick={() => openPdf('All', true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 text-white text-sm hover:bg-slate-900"
          >
            <FileDown size={14} /> Download All
          </button>
          <button
            data-testid="download-overview-pdf-btn"
            onClick={() => openOverviewPdf('All', true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-400 bg-white text-amber-900 text-sm hover:bg-amber-50"
            title="Download the condensed overview report"
          >
            <FileDown size={14} /> Download Overview
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Total Units (qty×lth)" value={fmtUnit(grandUnits)} />
        <Stat label="Total Labour $" value={fmtMoney(grandLabour)} accent="indigo" />
        <Stat label="Total Contractor $" value={fmtMoney(grandContractor)} accent="orange" />
      </div>

      {sourceBanner && (
        <div
          data-testid="contractor-source-banner"
          className={`rounded-md border px-3 py-2 text-xs ${sourceBanner.tone === 'amber'
            ? 'border-amber-300 bg-amber-50 text-amber-800'
            : 'border-rose-300 bg-rose-50 text-rose-800'}`}
        >
          {sourceBanner.text}
        </div>
      )}

      {/* Per-trade summary strip */}
      {summary.length > 0 && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <div className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 flex items-center justify-between">
            <span>Per-trade summary</span>
            <span className="text-[10px] text-slate-400 font-normal normal-case tracking-normal">Assign a contractor person and print/email the trade-specific sheet.</span>
          </div>
          <div className="divide-y divide-slate-100">
            {summary.map(sec => (
              <div key={sec.contractor_type} className="flex items-center justify-between flex-wrap gap-2 px-3 py-2" data-testid={`summary-row-${sec.contractor_type}`}>
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${SECTION_COLOURS[sec.contractor_type] || 'bg-slate-200 text-slate-700'}`}>
                    {sec.contractor_type}
                  </span>
                  <span className="text-xs text-slate-500">{sec.lines} line{sec.lines === 1 ? '' : 's'}</span>
                  <span className="text-xs text-slate-400">·</span>
                  <span className="text-xs text-slate-500">Units: <b className="text-slate-900 tabular-nums">{fmtUnit(sec.units)}</b></span>
                  <span className="text-xs text-slate-400">·</span>
                  <span className="text-xs text-slate-500">Con: <b className="text-orange-700 tabular-nums">{fmtMoney(sec.contractor_total)}</b></span>
                  {/* Iter274c — Acceptance status badge + manual override
                      buttons for THIS contractor section. */}
                  <AcceptanceBadge
                    ctype={sec.contractor_type}
                    acceptance={(data?.contractor_acceptances || {})[sec.contractor_type]}
                    apiBaseUrl={process.env.REACT_APP_BACKEND_URL}
                    appraisalId={appraisalId}
                    api={api}
                    onChange={load}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <select
                    data-testid={`assign-contractor-${sec.contractor_type}`}
                    value={sec.assigned_contractor_id || ''}
                    onChange={(e) => assignTypeContractor(sec.contractor_type, e.target.value)}
                    disabled={savingKeys[`type:${sec.contractor_type}`]}
                    className="text-xs border border-slate-300 rounded px-2 py-1 bg-white min-w-[160px]"
                  >
                    <option value="">— Unassigned —</option>
                    {contractors
                      .filter(c => !c.contractor_type || c.contractor_type === sec.contractor_type)
                      .map(c => (
                        <option key={c._id} value={c._id}>
                          {c.contractor_name || c.company_name}{c.gst_registered ? ' (GST)' : ''}
                        </option>
                      ))}
                  </select>
                  <button
                    data-testid={`preview-pdf-${sec.contractor_type}`}
                    onClick={() => openPdf(sec.contractor_type)}
                    title="Preview PDF"
                    className="text-xs px-1.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                  ><Eye size={12} /></button>
                  <button
                    data-testid={`download-pdf-${sec.contractor_type}`}
                    onClick={() => openPdf(sec.contractor_type, true)}
                    title="Download PDF"
                    className="text-xs px-1.5 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                  ><Download size={12} /></button>
                  <button
                    data-testid={`email-pdf-${sec.contractor_type}`}
                    onClick={() => setEmailModal({ ctype: sec.contractor_type, contractor_id: sec.assigned_contractor_id || '' })}
                    title="Email contractor"
                    className="text-xs px-1.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                  ><Mail size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Iter160 — Bulk actions bar (was column-header inputs in the
          old flat layout). Sits ABOVE the per-Component-Type sections
          so the section tables themselves stay clean and Quote-Calc-
          Summary–like. */}
      {liveRows.length > 0 && (
        <div className="border border-orange-200 bg-orange-50/40 rounded-md p-3" data-testid="contractor-bulk-actions">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-orange-900">Bulk Actions <span className="text-[10px] text-orange-700/70 font-normal normal-case tracking-normal">— apply across every row in every section</span></div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Iter269 — Per-row + Bulk Con $ override removed.
                  Contractor rate edits now happen exclusively through
                  the per-section "Edit rates" link (opens Component
                  Rates Tables), so the catalogue is the single source
                  of truth and appraisals don't drift. */}
              {/* Bulk Contractor Type */}
              <div className="flex items-center gap-1">
                <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Contractor Type</label>
                <select
                  data-testid="bulk-set-contractor-type"
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    if (val.startsWith('__force:')) bulkSetContractorType(val.replace('__force:', ''), true);
                    else bulkSetContractorType(val, false);
                    e.target.value = '';
                  }}
                  className="text-xs border border-orange-300 rounded px-2 py-1 bg-white text-orange-900 max-w-[160px]"
                >
                  <option value="">Change all ↓</option>
                  {CTYPES.map(t => <option key={t} value={t}>→ {t}</option>)}
                  <option disabled>──────────</option>
                  {CTYPES.map(t => <option key={`f-${t}`} value={`__force:${t}`}>↻ {t} (overwrite)</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Iter268b/c — Bulk Split sub-panel. Presets fill the form;
              Apply buttons fan-out across whole appraisal. */}
          <div className="mt-3 pt-3 border-t border-amber-300/60" data-testid="bulk-split-panel">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">Bulk Split</span>
              <span className="text-[10px] text-amber-700/80">— pick a preset or set custom, then Apply to scope</span>
            </div>
            {/* Preset chips */}
            <div className="flex items-center flex-wrap gap-1.5 mb-2" data-testid="split-presets">
              {SPLIT_PRESETS.map(p => (
                <button
                  key={p.label}
                  data-testid={`split-preset-${p.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                  onClick={() => setBulkSplit({ enabled: true, percent: p.percent, source_kind: p.source_kind, source_id: '' })}
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 transition-colors"
                >{p.label}</button>
              ))}
              <button
                data-testid="split-preset-clear"
                onClick={clearAllSplits}
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 transition-colors ml-1"
                title="Wipe split settings on every line"
              >× Clear all splits</button>
            </div>
            {/* Custom inputs + Apply scope buttons */}
            <div className="flex items-end flex-wrap gap-2">
              <div className="flex flex-col">
                <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">% to Con</label>
                <input
                  type="number" min={0} max={100} step={1}
                  data-testid="bulk-split-percent"
                  value={bulkSplit.percent}
                  onChange={(e) => setBulkSplit(b => ({ ...b, percent: e.target.value }))}
                  className="w-20 text-right text-xs border border-amber-300 rounded px-1.5 py-1 bg-white tabular-nums"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Source kind</label>
                <select
                  data-testid="bulk-split-kind"
                  value={bulkSplit.source_kind}
                  onChange={(e) => setBulkSplit(b => ({ ...b, source_kind: e.target.value, source_id: '' }))}
                  className="text-xs border border-amber-300 rounded px-2 py-1 bg-white"
                >
                  <option value="rtr">RTR</option>
                  <option value="person">Contractor (person)</option>
                  <option value="type">Contractor Type</option>
                </select>
              </div>
              {bulkSplit.source_kind === 'person' && (
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Pick contractor</label>
                  <select
                    data-testid="bulk-split-source-person"
                    value={bulkSplit.source_id}
                    onChange={(e) => setBulkSplit(b => ({ ...b, source_id: e.target.value }))}
                    className="text-xs border border-amber-300 rounded px-2 py-1 bg-white min-w-[150px]"
                  >
                    <option value="">—</option>
                    {(contractors || []).map(c => (
                      <option key={c._id || c.id} value={c._id || c.id}>{c.contractor_name || c.company_name || '(unnamed)'}</option>
                    ))}
                  </select>
                </div>
              )}
              {bulkSplit.source_kind === 'type' && (
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Pick type</label>
                  <select
                    data-testid="bulk-split-source-type"
                    value={bulkSplit.source_id}
                    onChange={(e) => setBulkSplit(b => ({ ...b, source_id: e.target.value }))}
                    className="text-xs border border-amber-300 rounded px-2 py-1 bg-white min-w-[150px]"
                  >
                    <option value="">—</option>
                    {CTYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-1 ml-1">
                <button
                  data-testid="bulk-split-apply-all"
                  onClick={() => applyBulkSplitPatch('all')}
                  className="text-[11px] font-bold px-3 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                  title="Apply this split to every line in every section of this appraisal"
                >Apply to whole appraisal</button>
                <select
                  data-testid="bulk-split-apply-section"
                  value=""
                  onChange={(e) => { const v = e.target.value; if (v) applyBulkSplitPatch('section', v); e.target.value = ''; }}
                  className="text-[11px] font-bold border border-amber-400 rounded px-2 py-1 bg-white text-amber-900 max-w-[180px]"
                  title="Apply this split to all lines of a specific Component Type section"
                >
                  <option value="">Apply to section ↓</option>
                  {componentSections.map(s => (
                    <option key={s.component_type} value={s.component_type}>{displayComponentType(s.component_type)} ({s.section_lines})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter160 — Per-Component-Type grouped sections (mirrors Quote
          Calculation Summary). One card per component_type, inner
          table groups by Type Name. Buy / Mat columns deliberately
          omitted — Contractor Overview only cares about Lab / Con. */}
      {liveRows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-12 text-center text-slate-400">
          No quote items, components, or diagram measurements found for this appraisal yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4" data-testid="contractor-work-list">
          {componentSections.map(sec => (
            <div key={sec.component_type} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`contractor-section-${sec.component_type}`}>
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-slate-700">{displayComponentType(sec.component_type)}</span>
                  <a
                    href={`/component-rates?component=${encodeURIComponent(sec.component_type)}`}
                    data-testid={`contractor-section-rates-link-${sec.component_type}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50"
                    title={`Open the ${displayComponentType(sec.component_type)} cascade in the Component Rates Tables (same window — use ← Back to return)`}
                  >
                    <Settings size={10} /> Edit rates <ChevronRight size={11} />
                  </a>
                </div>
                <span className="text-[11px] text-slate-500">
                  {sec.section_lines} line{sec.section_lines === 1 ? '' : 's'} · Units <b className="text-slate-900 tabular-nums">{fmtUnit(sec.section_units)}</b>
                  <span className="mx-2 text-slate-300">·</span>
                  Con <b className="text-orange-700 tabular-nums">{fmtMoney(sec.section_con)}</b>
                  {sec.section_excluded > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold border border-amber-300" title={`${sec.section_excluded} line${sec.section_excluded === 1 ? '' : 's'} hidden from the contractor PDF`}>
                      {sec.section_excluded} excluded from PDF
                    </span>
                  )}
                </span>
                {/* Iter268b — per-section split action chip. Applies the
                    current Bulk Split form (top of page) to every line
                    in this section only. */}
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`section-split-apply-${sec.component_type}`}
                    onClick={() => applyBulkSplitPatch('section', sec.component_type)}
                    className="text-[10px] font-bold px-2 py-0.5 rounded border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors"
                    title={`Apply the current Bulk Split (${bulkSplit.percent}% → ${bulkSplit.source_kind}) to every line in ${displayComponentType(sec.component_type)}`}
                  >Apply split ↓</button>
                  {(sec.section_split || 0) > 0 && (
                    <button
                      data-testid={`section-split-clear-${sec.component_type}`}
                      onClick={() => clearSectionSplits(sec.component_type)}
                      className="text-[10px] font-bold px-2 py-0.5 rounded border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 transition-colors"
                      title={`Clear splits on every line in ${displayComponentType(sec.component_type)}`}
                    >×</button>
                  )}
                </div>
              </div>
              {/* Iter258b — Contractor view drops Lab $/Lab Total columns.
                  Labour is internal cost (not contractor pay); leaking it
                  on the contractor sheet confuses pricing conversations.
                  Internal Lab figures still computed in the aggregation
                  (section_lab, labour_total) for the appraisal-level
                  Quote/Cost views — they're just not surfaced here. */}
              <div className="overflow-x-auto">
                {(() => {
                  // Iter258d — Dektite/Barge/Flashing get a split Dimension/Type
                  // pair; other sections keep the single Type column.
                  const splitType = _SECTIONS_TO_SPLIT.has(sec.component_type);
                  return (
                <table className="w-full text-xs" style={{ minWidth: splitType ? 1180 : 1100 }}>
                  <colgroup>
                    {/* Iter273 — Include-in-PDF checkbox column (~3%) */}
                    <col style={{ width: '3%' }} />
                    {splitType ? (
                      <>
                        {/* Iter262 — Type first (14%), Dimension second (18%). */}
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '14%' }} />
                      </>
                    ) : (
                      <col style={{ width: '24%' }} />
                    )}
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '9%' }} />
                    {/* Iter268 — Split column (toggle + % + source) */}
                    <col style={{ width: splitType ? '16%' : '18%' }} />
                    <col style={{ width: splitType ? '12%' : '12%' }} />
                  </colgroup>
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-orange-50/50">
                      <th className="text-center px-1 py-1.5" title="Include this row in the Contractor PDF. Untick to hide a line from the contractor (e.g. it's internal RTR work).">PDF</th>
                      {splitType ? (
                        <>
                          {/* Iter262 — Type column first per operator
                              walkthrough request. For Flashing rows
                              this reads the structured `flashing_type`
                              field (Back Tray / Apron / Change Of Pitch);
                              for other split sections it falls back to
                              the parsed subType from the catalogue name. */}
                          <th className="text-left px-2 py-1.5" title={sec.component_type === 'Flashing'
                                ? 'Flashing Type tagged on each component line (Back Tray / Apron / Change Of Pitch / etc.)'
                                : 'Sub-type classifier (Flue / Pipe / Corru / etc.) parsed from the trailing word(s) of the catalogue name'}>Type</th>
                          <th className="text-left px-2 py-1.5" title={`Dimension parsed from the catalogue name — ${displayComponentType(sec.component_type)} prefix + material words stripped`}>Dimension</th>
                        </>
                      ) : (
                        <th className="text-left px-2 py-1.5">Type</th>
                      )}
                      <th className="text-left px-2 py-1.5">Profile</th>
                      {/* Iter175 — Qty + Length surfaced alongside Total */}
                      <th className="text-right px-2 py-1.5" title="Sum of line quantities">Qty</th>
                      <th className="text-right px-2 py-1.5" title="Sum of line lengths (m)">Length</th>
                      <th className="text-right px-2 py-1.5">Total</th>
                      <th className="text-right px-2 py-1.5">Con $</th>
                      <th className="text-right px-2 py-1.5 bg-orange-50/60">Con Total</th>
                      <th className="text-left px-2 py-1.5 bg-amber-50/60" title="Contractor Split — by default 100% goes to the assigned contractor. Tick to enable, then enter the contractor's percentage; the remainder is owed to the Split Source.">Split</th>
                      <th className="text-left px-2 py-1.5">Contractor Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sec.type_rows.map((tr, i) => {
                      const tRowKey = `${sec.component_type}__${tr.type_name}`;
                      const parsed = splitType ? parseTypeName(sec.component_type, tr.type_name) : null;
                      return (
                        <tr key={tRowKey}
                            className={`${tr.isDirty ? 'bg-yellow-50' : 'hover:bg-slate-50'} ${tr.excluded_from_report === true ? 'opacity-50 line-through-row' : ''}`}
                            data-testid={`contractor-type-row-${tRowKey}`}>
                          {/* Iter273 — Include-in-PDF checkbox. Mixed
                              state across underlying lines shows an
                              amber indeterminate box; toggling fans
                              the patch out to every underlying line. */}
                          <td className="px-1 py-1.5 text-center align-top">
                            <input
                              type="checkbox"
                              data-testid={`include-in-pdf-${tRowKey}`}
                              ref={el => { if (el) el.indeterminate = (tr.excluded_from_report === null); }}
                              checked={tr.excluded_from_report === false}
                              onChange={(e) => setTypeRowField(tr, 'excluded_from_report', !e.target.checked)}
                              className="accent-orange-600 w-4 h-4 cursor-pointer"
                              title={tr.excluded_from_report === null
                                ? 'Mixed — some underlying lines are excluded. Tick to include all; untick to exclude all.'
                                : (tr.excluded_from_report
                                    ? 'EXCLUDED from contractor PDF — click to include'
                                    : 'Included in contractor PDF — click to exclude')}
                            />
                            {tr.excluded_count > 0 && tr.excluded_from_report === null && (
                              <div className="text-[9px] font-bold text-amber-700 leading-none mt-0.5" title={`${tr.excluded_count} of ${tr.line_count} lines excluded`}>{tr.excluded_count}/{tr.line_count}</div>
                            )}
                          </td>
                          {splitType ? (
                            <>
                              {/* Iter262 — Type cell first. For Flashing
                                  rows this reads `flashing_type` (Back
                                  Tray / Apron / Change Of Pitch / etc.);
                                  for Dektite/Barge it falls back to the
                                  parsed subType from the catalogue name. */}
                              <td className="px-2 py-1.5 text-slate-600 truncate" title={tr.type_name} data-testid={`contractor-row-subtype-${tRowKey}`}>
                                {sec.component_type === 'Flashing'
                                  ? (tr.flashing_type || <span className="text-slate-300">—</span>)
                                  : (parsed.subType || <span className="text-slate-300">—</span>)}
                                {sec.component_type === 'Flashing' && tr.lines.some(l => l.flashing_spec) && (
                                  <div className="text-[10px] text-slate-500 whitespace-normal leading-snug mt-0.5" data-testid={`contractor-row-flashing-spec-${tRowKey}`}>
                                    {tr.lines.filter(l => l.flashing_spec).map(l => <div key={l.line_key}>{l.ref ? `${l.ref} · ` : ''}{l.flashing_spec}</div>)}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-slate-700 font-medium font-mono-num">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="truncate" title={tr.type_name} data-testid={`contractor-row-dim-${tRowKey}`}>
                                    {parsed.dimension || '—'}
                                  </span>
                                  {tr.line_count > 1 && (
                                    <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 tabular-nums" title={`${tr.line_count} component lines roll up into this row`}>
                                      ×{tr.line_count}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </>
                          ) : (
                            <td className="px-2 py-1.5 text-slate-700 font-medium">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="truncate" title={tr.type_name}>{tr.type_name}</span>
                                {tr.line_count > 1 && (
                                  <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 tabular-nums" title={`${tr.line_count} component lines roll up into this row`}>
                                    ×{tr.line_count}
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-slate-500 truncate">{tr.profile || '—'}</td>
                          {/* Iter175 — Qty + Length cells. total_qty /
                              total_length are summed across the
                              underlying component lines in the type-row
                              aggregation (line 208-209 in the loop). */}
                          <td
                            className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums"
                            data-testid={`contractor-type-qty-${tRowKey}`}
                            title="Total Qty (sum of line quantities)"
                          >
                            {fmtUnit(tr.total_qty)}
                          </td>
                          <td
                            className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums"
                            data-testid={`contractor-type-length-${tRowKey}`}
                            title="Total Length (sum of line lengths in metres)"
                          >
                            {fmtUnit(tr.total_length)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-800 tabular-nums">{fmtUnit(tr.total_units)}</td>
                          {/* Iter269 — Con $ rate is now READ-ONLY at
                              the appraisal level. Rate changes are
                              made through the per-section "Edit rates"
                              link (opens the Component Rates Tables
                              page) so a single source of truth lives
                              in the catalogue, not on the appraisal. */}
                          <td className="px-2 py-1.5 text-right font-mono text-slate-600 tabular-nums" data-testid={`con-rate-display-${tRowKey}`}
                              title={tr.contractor_rate === null ? 'Underlying lines have mixed Con rates — open Edit rates to unify.' : 'Read-only. Edit via the section "Edit rates" link.'}>
                            {tr.contractor_rate === null ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">mixed</span> : fmtMoney(tr.contractor_rate)}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold text-orange-700 bg-orange-50/30 tabular-nums">{fmtMoney(tr.contractor_total)}</td>
                          {/* Iter268 — Split cell. Checkbox + percent
                              input + source kind / source id picker.
                              Mixed-line states (different percents
                              across underlying lines) render as a
                              dashed amber pill. */}
                          <td className="px-2 py-1.5 bg-amber-50/40" data-testid={`split-cell-${tRowKey}`}>
                            <SplitCell
                              tRow={tr}
                              contractors={contractors}
                              currentContractorTypeId={(data?.type_assignments || {})[tr.contractor_type] || ''}
                              onChange={(patch) => setLinesSplit(tr.lines, patch)}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              data-testid={`reassign-${tRowKey}`}
                              value={tr.contractor_type || ''}
                              onChange={(e) => setTypeRowField(tr, 'contractor_type', e.target.value)}
                              className={`w-full text-xs border rounded px-1 py-0.5 ${tr.contractor_type === null ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                              title={tr.contractor_type === null ? 'Mixed across underlying lines — pick one to unify' : 'Reassign this Work Type to a different Contractor Type'}
                            >
                              {tr.contractor_type === null && <option value="">Mixed — pick one</option>}
                              {CTYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td colSpan={splitType ? 4 : 3} className="px-2 py-2 text-right text-xs font-bold text-slate-500">{displayComponentType(sec.component_type)} subtotal</td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-[#D35400] tabular-nums">{fmtUnit(sec.section_units)}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-orange-700 tabular-nums">{fmtMoney(sec.section_con)}</td>
                      <td className="px-2 py-2 bg-amber-50/30 text-xs text-amber-800">
                        {(sec.section_split || 0) > 0 ? (
                          <div className="flex flex-col leading-tight">
                            <span className="font-bold tabular-nums">Net {fmtMoney(sec.section_net)}</span>
                            <span className="text-amber-700 tabular-nums">Split {fmtMoney(sec.section_split)}</span>
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}

      {emailModal && (
        <EmailContractorModal
          api={api}
          appraisalId={appraisalId}
          ctype={emailModal.ctype}
          contractors={contractors}
          initialContractorId={emailModal.contractor_id}
          appraisalNo={data.appraisal_no}
          onClose={() => setEmailModal(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const accentClass = accent === 'orange' ? 'text-orange-700'
    : accent === 'indigo' ? 'text-indigo-700'
    : accent === 'emerald' ? 'text-emerald-700'
    : accent === 'rose' ? 'text-rose-700'
    : 'text-slate-900';
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className={`text-lg font-bold ${accentClass} tabular-nums`}>{value}</div>
    </div>
  );
}

function WorkDescriptionPicker({ row, fetchRateOptions, onPick, onTextChange }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const openDropdown = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (opts.length === 0 && row.component) {
      setLoading(true);
      const r = await fetchRateOptions(row.component);
      setOpts(r);
      setLoading(false);
    }
  };

  const filtered = filter
    ? opts.filter(o => (o.name || '').toLowerCase().includes(filter.toLowerCase()))
    : opts;

  return (
    <div className="relative" data-testid={`work-desc-picker-${row.line_key}`}>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={row.description}
          onChange={(e) => onTextChange(e.target.value)}
          className="flex-1 min-w-0 border border-slate-200 rounded px-1.5 py-0.5 text-xs"
          data-testid={`work-desc-input-${row.line_key}`}
        />
        <button
          type="button"
          onClick={openDropdown}
          className="flex-shrink-0 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-100"
          title="Pick from rate-table"
          data-testid={`work-desc-pick-${row.line_key}`}
        >
          <ChevronsUpDown size={12} className="text-slate-500" />
        </button>
      </div>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-[420px] max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Filter ${row.component || ''} rate rows…`}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1"
            />
            <div className="text-[10px] text-slate-400 mt-1">{filtered.length} option{filtered.length === 1 ? '' : 's'} from <code className="font-mono">{row.component?.toLowerCase()}</code> rate table</div>
          </div>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-400">
              No rate-table entries for <code>{row.component?.toLowerCase()}</code>. Add some via <b>Manage Rate Tables</b>.
            </div>
          )}
          {filtered.map((o, idx) => (
            <button
              key={`${o.name}-${idx}`}
              type="button"
              onClick={() => { onPick(o); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 hover:bg-orange-50 border-b border-slate-100 last:border-0"
              data-testid={`work-desc-opt-${row.line_key}-${idx}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-800 truncate flex-1">{o.name}</span>
                <span className="text-[10px] tabular-nums text-indigo-700 flex-shrink-0">Lab {fmtMoney(o.labour_rate)}</span>
                <span className="text-[10px] tabular-nums text-orange-700 flex-shrink-0">Con {fmtMoney(o.contractor_rate)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailContractorModal({ api, appraisalId, ctype, contractors, initialContractorId, appraisalNo, onClose }) {
  const [contractorId, setContractorId] = useState(initialContractorId || '');
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState(`Contractor Sheet — Appraisal ${appraisalNo || ''} (${ctype})`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const c = contractors.find(x => x._id === contractorId);
    if (c) setToEmail(c.email || '');
  }, [contractorId, contractors]);

  const send = async () => {
    if (!toEmail) { toast.error('Recipient email required'); return; }
    setSending(true);
    try {
      const res = await api.post(`/appraisals/${appraisalId}/contractor-pdf/${encodeURIComponent(ctype)}/email`, {
        contractor_id: contractorId || undefined,
        to_email: toEmail,
        subject,
        message: message || undefined,
      });
      const r = res.data || res;
      if (r.sent) { toast.success(`Sent to ${r.to_email}`); onClose(); }
      else        { toast.warning(r.note || r.error || 'Email logged but not sent.'); }
    } catch (err) {
      toast.error(`Send failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="email-contractor-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-5">
        <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Mail size={18} className="text-emerald-600" /> Email {ctype} Contractor
        </h3>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold text-slate-600">Contractor</label>
            <select
              data-testid="email-contractor-picker"
              value={contractorId}
              onChange={(e) => setContractorId(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 mt-1"
            >
              <option value="">— Custom email below —</option>
              {contractors
                .filter(c => !c.contractor_type || c.contractor_type === ctype)
                .map(c => (
                  <option key={c._id} value={c._id}>
                    {c.contractor_name || c.company_name}{c.email ? ` <${c.email}>` : ''}{c.gst_registered ? ' (GST)' : ''}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">To</label>
            <input
              data-testid="email-to-input"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Subject</label>
            <input
              data-testid="email-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Message (optional)</label>
            <textarea
              data-testid="email-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 rounded px-2 py-1.5 mt-1"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="email-cancel-btn" onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 text-sm hover:bg-slate-100">Cancel</button>
          <button data-testid="email-send-btn" onClick={send} disabled={sending} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50">{sending ? 'Sending…' : 'Send Email'}</button>
        </div>
      </div>
    </div>
  );
}
